import { t } from "../i18n/runtime";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  companyStorageIsTemporary,
  fixBrowserCompanyName,
} from "../companyIdentity";
import { startRefusalMessage } from "../i18n";
import { startSession } from "../network/api";
import { useGameStore } from "../stores/gameStore";
import { shouldAutoStart } from "./gateStart";
import type { GatePhase } from "./gateStart";
import { Button } from "./primitives";

/** The one-time step between the briefing and the first simulation tick. */
export function CompanyNameGate() {
  const dismissed = useGameStore((state) => state.tutorialDismissed);
  const sessionId = useGameStore((state) => state.sessionId);
  const companyName = useGameStore((state) => state.companyName);
  const setCompanyName = useGameStore((state) => state.setCompanyName);
  const [draft, setDraft] = useState("");
  const [phase, setPhase] = useState<GatePhase>("waiting");
  const [error, setError] = useState<string | null>(null);
  const inFlight = useRef(false);

  const begin = useCallback(async (): Promise<void> => {
    if (!sessionId || inFlight.current) return;
    inFlight.current = true;
    setPhase("starting");
    setError(null);

    const started = await startSession(sessionId);
    if (!started.ok) {
      inFlight.current = false;
      setPhase("waiting");
      setError(startRefusalMessage(started.error));
      return;
    }
    setPhase("started");
  }, [sessionId]);

  useEffect(() => {
    if (shouldAutoStart({ dismissed, companyName, phase, error })) void begin();
  }, [begin, companyName, dismissed, phase, error]);

  const storageNotice = companyStorageIsTemporary() ? (
    <div
      role="status"
      style={{
        position: "fixed",
        bottom: 8,
        left: 8,
        zIndex: 1000,
        background: "#17202c",
        color: "white",
        padding: 8,
      }}
    >
      {t("CompanyNameGate.storage_is_blocked_your_company_name_will")}
    </div>
  ) : null;
  if (!dismissed || phase === "started") return storageNotice;

  const submit = (): void => {
    if (companyName) {
      void begin();
      return;
    }
    const fixed = fixBrowserCompanyName(draft);
    if (!fixed.ok) {
      setError(
        fixed.error === "invalid company name"
          ? t("CompanyNameGate.enter_a_company_name_of_characters_without")
          : t("CompanyNameGate.could_not_save_your_company_name_in"),
      );
      return;
    }
    setCompanyName(fixed.companyName);
    void begin();
  };

  return (
    <div className="company-veil">
      {storageNotice}
      <aside
        className="company-gate"
        role="dialog"
        aria-modal="true"
        aria-labelledby="company-title"
      >
        <div className="label">{t("CompanyNameGate.broker_registration")}</div>
        <h2 className="company-gate__title" id="company-title">
          {companyName
            ? t("CompanyNameGate.is_preparing_to_sail", { p0: companyName })
            : t("CompanyNameGate.choose_your_company_name")}
        </h2>
        <p className="company-gate__copy">
          {companyName
            ? t("CompanyNameGate.sailing_with_your_saved_company_name")
            : t("CompanyNameGate.this_name_will_be_used_for_every")}
        </p>

        {!companyName ? (
          <label className="company-gate__field">
            <span>{t("CompanyNameGate.company_name")}</span>
            <input
              autoFocus
              maxLength={20}
              value={draft}
              onChange={(event) => setDraft(event.currentTarget.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") submit();
              }}
              placeholder={t("CompanyNameGate.example_blue_sea_trading")}
              aria-invalid={error !== null}
            />
            <small>{draft.length} / 20</small>
          </label>
        ) : null}

        {error ? <div className="company-gate__error">{error}</div> : null}

        <div className="company-gate__foot">
          <span className="muted">
            {t(
              "CompanyNameGate.the_server_calculates_the_final_score_directly",
            )}
          </span>
          <Button
            tone="primary"
            disabled={
              phase === "starting" || (!companyName && draft.trim() === "")
            }
            onClick={submit}
          >
            {phase === "starting"
              ? t("CompanyNameGate.sailing")
              : companyName
                ? t("CompanyNameGate.try_again")
                : t("CompanyNameGate.start_with_this_name")}
          </Button>
        </div>
      </aside>
    </div>
  );
}
